import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { createClientComponentClient } from '@/integrations/supabase/client'
import { Tables } from '@/integrations/supabase/types'

export interface SubjectProgress {
    id: string
    user_id: string
    category_id: string
    class_id: string | null
    hub_id: string | null
    current_unit: number
    current_lesson: number
    lessons_completed: string[]   // array of lesson_ids
    mastery_score: number
    quiz_history: QuizHistorySummary[]
    weak_concepts: string[]
    review_queue: string[]
    ai_generated_path: unknown[]
    achievements: string[]
    started_at: string
    last_activity_at: string
    completed_at: string | null
    // Joined from categories
    category_name: string
    category_icon: string
    category_slug: string
    educational_level: string | null
    class_name: string | null
    essay_mode?: string | null
    module_plan?: any[] | null
    enrollment_type?: 'assigned' | 'self_enrolled'
    assignment_mode?: 'entire_org' | 'targeted' | 'self_enrolled'
}

export interface QuizResult {
    id: string
    progress_id: string | null
    user_id: string | null
    class_id: string | null
    category_id: string | null
    lesson_unit: number
    lesson_number: number
    questions: QuizQuestion[]
    score_percentage: number
    duration_seconds: number | null
    attempted_at: string
    // Joined
    category_name?: string
}

export interface QuizQuestion {
    question: string
    options: string[]
    correct: string
    student_answer: string
    correct_yn: boolean
}

export interface QuizHistorySummary {
    score: number
    at: string
}

export interface StudentProgressData {
    subjects: SubjectProgress[]
    quizResults: QuizResult[]
    streakDays: number
    totalDaysActive: number
    subjectsMastered: number
    loading: boolean
    error: string | null
    refetch: () => void
    fetchLessonLogs: (categoryId?: string, classId?: string | null) => Promise<any[]>
    loadSubjectPlan: (classId: string) => Promise<void>
}

/**
 * Returns a YYYY-MM-DD string in UTC for a given Date.
 * Using toDateString() would shift dates for users not in UTC.
 */
function toUTCDateKey(d: Date): string {
    return d.toISOString().slice(0, 10)
}

/**
 * Computes streak from last_activity_at timestamps in subjects.
 * Returns number of consecutive calendar days (UTC) up to today.
 */
function computeStreak(subjects: SubjectProgress[]): number {
    const activityDates = subjects
        .map(s => s.last_activity_at ? toUTCDateKey(new Date(s.last_activity_at)) : null)
        .filter(Boolean) as string[]

    const uniqueDates = [...new Set(activityDates)].sort((a, b) =>
        b.localeCompare(a)   // ISO strings sort correctly lexicographically
    )

    if (uniqueDates.length === 0) return 0

    let streak = 0
    const today = new Date()
    for (let i = 0; i < uniqueDates.length; i++) {
        const expected = new Date(today)
        expected.setUTCDate(today.getUTCDate() - i)
        if (uniqueDates[i] === toUTCDateKey(expected)) {
            streak++
        } else {
            break
        }
    }
    return streak
}

export function useStudentProgress(): StudentProgressData {
    const { user, userProfile } = useAuth()
    // Stabilise the supabase client reference — createClientComponentClient() can
    // return a new object each call, which would make fetchAll's deps change every
    // render and trigger an infinite fetch loop.
    const supabaseRef = useRef(createClientComponentClient())
    const supabase = supabaseRef.current

    const [subjects, setSubjects] = useState<SubjectProgress[]>([])
    const [quizResults, setQuizResults] = useState<QuizResult[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const fetchAll = useCallback(async () => {
        if (!user) { setLoading(false); return }
        setLoading(true)
        setError(null)

        try {
            const userFacilityId = userProfile?.facility_id
            const isOrgMember = !!userFacilityId && !userFacilityId.startsWith('00000000-')

            // 1. Student subject progress — existing progress rows for this user
            const { data: progData, error: progErr } = await supabase
                .from('student_subject_progress')
                .select(`
                    id, user_id, category_id, class_id, hub_id,
                    current_unit, current_lesson, lessons_completed,
                    mastery_score, quiz_history, weak_concepts, review_queue,
                    ai_generated_path, achievements, started_at, last_activity_at, completed_at
                `)
                .eq('user_id', user.id)
                .order('last_activity_at', { ascending: false })

            if (progErr) throw progErr

            const progRows = (progData || [])
            const progressByClassId: Record<string, typeof progRows[0]> = {}
            for (const p of progRows) {
                if (p.class_id) progressByClassId[p.class_id] = p
            }

            // 2. Facility-wide visibility: query active published classes in member's facility
            let facilityClasses: any[] = []
            if (isOrgMember) {
                const { data: fCls, error: fClsErr } = await (supabase
                    .from('classes') as any)
                    .select('id, name, category_id, facility_id, essay_mode, is_published, enrollment_mode, module_plan')
                    .eq('facility_id', userFacilityId)
                    .eq('is_published', true)
                    .eq('is_active', true)
                    .eq('enrollment_mode', 'all')
                    .order('name')
                if (!fClsErr && fCls) facilityClasses = fCls
            }

            // 2b. Enrollments: fetch all classes the student is enrolled in
            const { data: enrolled } = await supabase
                .from('class_enrollments')
                .select('class_id')
                .eq('student_id', user.id)
            const enrolledIds = (enrolled || []).map((e: any) => e.class_id).filter(Boolean)

            let targetedClasses: any[] = []
            if (isOrgMember && enrolledIds.length > 0) {
                const { data: tCls } = await (supabase.from('classes') as any)
                    .select('id, name, category_id, facility_id, essay_mode, is_published, enrollment_mode, module_plan')
                    .in('id', enrolledIds)
                    .eq('facility_id', userFacilityId)
                    .eq('enrollment_mode', 'targeted')
                    .eq('is_published', true)
                    .eq('is_active', true)
                if (tCls) targetedClasses = tCls
            }

            // Catalog electives: any class_enrollments not belonging to the member's facility
            let catalogEnrolledClasses: any[] = []
            const nonFacilityEnrolledIds = enrolledIds.filter(id =>
                !facilityClasses.some(fc => fc.id === id) &&
                !targetedClasses.some(tc => tc.id === id)
            )
            if (nonFacilityEnrolledIds.length > 0) {
                const { data: cCls } = await (supabase.from('classes') as any)
                    .select('id, name, category_id, facility_id, essay_mode, is_published, enrollment_mode, module_plan')
                    .in('id', nonFacilityEnrolledIds)
                    .eq('is_active', true)
                // Catalog electives are classes owned by OTHER facilities (master hub,
                // university, community seeds). Anything owned by THIS member's facility
                // landing here means it fell out of the assigned buckets above — e.g. a
                // rostered targeted course that is currently unpublished. That is not a
                // self-enrollment: drop it so drafts stay invisible to learners.
                if (cCls) catalogEnrolledClasses = cCls.filter((c: any) =>
                    !userFacilityId || String(c.facility_id) !== String(userFacilityId))
            }

            // 3. Class metadata map (for names, essay modes, and module plans)
            const knownClassMap: Record<string, {
                name: string;
                essay_mode: string | null;
                module_plan: any[] | null;
                is_published: boolean;
                enrollment_mode: string;
                facility_id?: string | null;
            }> = {}

            for (const fc of [...facilityClasses, ...targetedClasses, ...catalogEnrolledClasses]) {
                knownClassMap[fc.id] = {
                    name: fc.name,
                    essay_mode: fc.essay_mode ?? null,
                    module_plan: fc.module_plan ? (Array.isArray(fc.module_plan) ? fc.module_plan : null) : null,
                    is_published: fc.is_published ?? true,
                    enrollment_mode: fc.enrollment_mode ?? 'all',
                    facility_id: fc.facility_id ?? null,
                }
            }

            // Also resolve metadata for any other self-enrolled or legacy classes in progRows
            const extraClassIds = [...new Set(progRows.map(r => r.class_id).filter(cid => cid && !knownClassMap[cid]))] as string[]
            if (extraClassIds.length > 0) {
                const { data: extraRows } = await (supabase
                    .from('classes') as any)
                    .select('id, name, essay_mode, is_published, enrollment_mode, facility_id')
                    .in('id', extraClassIds)
                if (extraRows && Array.isArray(extraRows)) {
                    for (const c of extraRows) {
                        knownClassMap[c.id] = {
                            name: c.name,
                            essay_mode: c.essay_mode ?? null,
                            module_plan: null,
                            is_published: c.is_published ?? true,
                            enrollment_mode: c.enrollment_mode ?? 'all',
                            facility_id: c.facility_id ?? null,
                        }
                    }
                }
                const essayIds = (extraRows || []).filter((c: any) => c.essay_mode === 'essay_per_module').map((c: any) => c.id)
                if (essayIds.length > 0) {
                    const { data: planRows } = await supabase
                        .from('classes')
                        .select('id, module_plan')
                        .in('id', essayIds)
                    for (const p of planRows || []) {
                        if (knownClassMap[p.id]) knownClassMap[p.id].module_plan = (p.module_plan as any[]) ?? null
                    }
                }
            }

            // 4. Construct unified rows:
            // (a) All published 'all' facility classes + targeted classes (use existing progress, else lazy 0%)
            const combinedRows: any[] = []
            const handledClassIds = new Set<string>()

            for (const fc of facilityClasses) {
                handledClassIds.add(fc.id)
                const existingProg = progressByClassId[fc.id]
                if (existingProg) {
                    combinedRows.push({ ...existingProg, enrollment_type: 'assigned', assignment_mode: 'entire_org' })
                } else {
                    combinedRows.push({
                        id: `fc-${fc.id}`,
                        user_id: user.id,
                        category_id: fc.category_id,
                        class_id: fc.id,
                        hub_id: userFacilityId,
                        current_unit: 1,
                        current_lesson: 1,
                        lessons_completed: [],
                        mastery_score: 0,
                        quiz_history: [],
                        weak_concepts: [],
                        review_queue: [],
                        ai_generated_path: [],
                        achievements: [],
                        started_at: null,
                        last_activity_at: null,
                        completed_at: null,
                        enrollment_type: 'assigned',
                        assignment_mode: 'entire_org',
                    })
                }
            }

            for (const tc of targetedClasses) {
                handledClassIds.add(tc.id)
                const existingProg = progressByClassId[tc.id]
                if (existingProg) {
                    combinedRows.push({ ...existingProg, enrollment_type: 'assigned', assignment_mode: 'targeted' })
                } else {
                    combinedRows.push({
                        id: `fc-${tc.id}`,
                        user_id: user.id,
                        category_id: tc.category_id,
                        class_id: tc.id,
                        hub_id: userFacilityId,
                        current_unit: 1,
                        current_lesson: 1,
                        lessons_completed: [],
                        mastery_score: 0,
                        quiz_history: [],
                        weak_concepts: [],
                        review_queue: [],
                        ai_generated_path: [],
                        achievements: [],
                        started_at: null,
                        last_activity_at: null,
                        completed_at: null,
                        enrollment_type: 'assigned',
                        assignment_mode: 'targeted',
                    })
                }
            }

            // (b) Catalog electives self-enrolled by the user
            for (const cc of catalogEnrolledClasses) {
                handledClassIds.add(cc.id)
                const existingProg = progressByClassId[cc.id]
                if (existingProg) {
                    combinedRows.push({ ...existingProg, enrollment_type: 'self_enrolled', assignment_mode: 'self_enrolled' })
                } else {
                    combinedRows.push({
                        id: `ce-${cc.id}`,
                        user_id: user.id,
                        category_id: cc.category_id,
                        class_id: cc.id,
                        hub_id: cc.facility_id || null,
                        current_unit: 1,
                        current_lesson: 1,
                        lessons_completed: [],
                        mastery_score: 0,
                        quiz_history: [],
                        weak_concepts: [],
                        review_queue: [],
                        ai_generated_path: [],
                        achievements: [],
                        started_at: null,
                        last_activity_at: null,
                        completed_at: null,
                        enrollment_type: 'self_enrolled',
                        assignment_mode: 'self_enrolled',
                    })
                }
            }

            // (c) Any other self-enrolled or legacy classes/subjects from progRows
            // Gated strictly on enrollment: if a class belongs to the member's facility
            // but is not in handledClassIds (i.e. learner is not enrolled or course is unpublished),
            // do NOT resurrect it via residual student_subject_progress rows.
            for (const r of progRows) {
                if (r.class_id && handledClassIds.has(r.class_id)) continue
                if (r.class_id) {
                    const meta = knownClassMap[r.class_id]
                    if (meta && meta.facility_id === userFacilityId) continue
                    if (meta && meta.is_published === false) continue
                    if (meta && meta.enrollment_mode === 'targeted') continue
                }
                combinedRows.push({ ...r, enrollment_type: 'self_enrolled', assignment_mode: 'self_enrolled' })
            }

            // 5. Enrich with category names & icons
            const categoryIds = [...new Set(combinedRows.map(r => r.category_id).filter(Boolean))]
            let categoryMap: Record<string, { name: string; icon: string; slug: string; educational_level: string | null }> = {}

            if (categoryIds.length > 0) {
                const { data: catData } = await supabase
                    .from('categories')
                    .select('id, name, icon, slug, educational_level')
                    .in('id', categoryIds)

                if (catData) {
                    for (const c of catData) {
                        categoryMap[c.id] = {
                            name: c.name,
                            icon: c.icon || '📚',
                            slug: c.slug || (c.name ? c.name.toLowerCase().replace(/&/g, 'and').replace(/[\/]+/g, '-').replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') : ''),
                            educational_level: c.educational_level ?? null,
                        }
                    }
                }
            }

            const enriched: SubjectProgress[] = combinedRows.map(r => ({
                id: r.id,
                user_id: r.user_id,
                category_id: r.category_id,
                class_id: r.class_id,
                hub_id: r.hub_id,
                current_unit: r.current_unit ?? 1,
                current_lesson: r.current_lesson ?? 1,
                mastery_score: r.mastery_score ?? 0,
                started_at: r.started_at ?? new Date().toISOString(),
                last_activity_at: r.last_activity_at ?? new Date().toISOString(),
                completed_at: r.completed_at,
                lessons_completed: Array.isArray(r.lessons_completed) ? r.lessons_completed as string[] : [],
                quiz_history: Array.isArray(r.quiz_history) ? (r.quiz_history as unknown as QuizHistorySummary[]) : [],
                weak_concepts: Array.isArray(r.weak_concepts) ? r.weak_concepts as string[] : [],
                review_queue: Array.isArray(r.review_queue) ? r.review_queue as string[] : [],
                ai_generated_path: Array.isArray(r.ai_generated_path) ? r.ai_generated_path as unknown[] : [],
                achievements: Array.isArray(r.achievements) ? r.achievements as string[] : [],
                category_name: categoryMap[r.category_id]?.name ?? 'Unknown Subject',
                category_icon: categoryMap[r.category_id]?.icon ?? '📚',
                category_slug: categoryMap[r.category_id]?.slug ?? '',
                educational_level: categoryMap[r.category_id]?.educational_level ?? null,
                class_name: r.class_id ? knownClassMap[r.class_id]?.name ?? null : null,
                essay_mode: r.class_id ? knownClassMap[r.class_id]?.essay_mode ?? null : null,
                module_plan: r.class_id ? knownClassMap[r.class_id]?.module_plan ?? null : (Array.isArray(r.ai_generated_path) ? r.ai_generated_path as any[] : null),
                enrollment_type: r.enrollment_type ?? 'self_enrolled',
                assignment_mode: r.assignment_mode ?? 'self_enrolled',
            }))

            setSubjects(enriched)

            // 3. Quiz results — most recent 50 for this user
            const { data: quizData } = await supabase
                .from('education_quiz_results')
                .select(`
                    id, progress_id, user_id, class_id, category_id,
                    lesson_unit, lesson_number, questions, score_percentage,
                    duration_seconds, attempted_at
                `)
                .eq('user_id', user.id)
                .order('attempted_at', { ascending: false })
                .limit(50)

            // Enrich quiz results with category names from the map we already built
            const enrichedQuiz: QuizResult[] = (quizData || []).map(q => ({
                id: q.id,
                progress_id: q.progress_id,
                user_id: q.user_id,
                class_id: q.class_id,
                category_id: q.category_id,
                lesson_unit: q.lesson_unit,
                lesson_number: q.lesson_number,
                score_percentage: q.score_percentage,
                duration_seconds: q.duration_seconds,
                attempted_at: q.attempted_at ?? new Date().toISOString(),
                questions: Array.isArray(q.questions) ? (q.questions as unknown as QuizQuestion[]) : [],
                category_name: q.category_id ? (categoryMap[q.category_id]?.name ?? 'Subject') : 'Subject',
            }))

            setQuizResults(enrichedQuiz)
        } catch (err: unknown) {
            console.error('[useStudentProgress]', err)
            setError((err as Error).message || 'Failed to load progress.')
        } finally {
            setLoading(false)
        }
    }, [user?.id, userProfile?.facility_id])

    useEffect(() => {
        fetchAll()
    }, [fetchAll])

    // Metrics only from courses with real activity — placeholder rows (fc-/ce- ids)
    // carry synthetic today-timestamps and would inflate streak / days-active.
    const realSubjects = subjects.filter(s => !String(s.id).startsWith('fc-') && !String(s.id).startsWith('ce-'))
    const streakDays = computeStreak(realSubjects)
    const totalDaysActive = new Set(
        realSubjects
            .map(s => s.last_activity_at ? toUTCDateKey(new Date(s.last_activity_at)) : null)
            .filter(Boolean)
    ).size
    const subjectsMastered = subjects.filter(s => s.mastery_score >= 90).length

    const fetchLessonLogs = useCallback(async (categoryId?: string, classId?: string | null) => {
        if (!user) return []
        let q = supabase
            .from('student_lesson_logs')
            .select('*')
            .eq('user_id', user.id)
            .order('completed_at', { ascending: false })
            .limit(10)

        if (categoryId) q = q.eq('category_id', categoryId)
        if (classId) q = q.eq('class_id', classId)

        const { data, error } = await q
        if (error) throw error
        return data || []
    }, [user, supabase])

    // ── On-demand module_plan fetch (payload law / Knowledge Tree) ──
    const loadSubjectPlan = useCallback(async (classId: string) => {
        const { data } = await supabase
            .from('classes')
            .select('id, module_plan')
            .eq('id', classId)
            .maybeSingle()
        if (data?.module_plan) {
            setSubjects(prev => prev.map(s =>
                s.class_id === classId
                    ? { ...s, module_plan: data.module_plan as any[] }
                    : s
            ))
        }
    }, [supabase])

    return {
        subjects,
        quizResults,
        streakDays,
        totalDaysActive,
        subjectsMastered,
        loading,
        error,
        refetch: fetchAll,
        fetchLessonLogs,
        loadSubjectPlan,
    }
}
